import { useTranslation } from "react-i18next";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { Controller, useFormContext } from "react-hook-form";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Box, FormLabel, Typography, Checkbox } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { days, DayType } from "../../../../enums/days.enum";

import { EntityBranchFormData } from "../../../Entity/types/entityBranch.types";

const formatTimeString = (date: Date | null): string => {
  if (!date) return "00:00";
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

const WorkingHoursTab = ({ isBranchDataLoading }: { isBranchDataLoading: boolean }) => {
  const { t } = useTranslation("profiles", { keyPrefix: "entityProfileEditBranch" });

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>{t("workingHours")}</Typography>
      {days.map((day) => (
        <WorkingHoursField key={day} day={day as DayType} isBranchDataLoading={isBranchDataLoading} />
      ))}
    </Box>
  );
};

export default WorkingHoursTab;

const WorkingHoursField = ({ day, isBranchDataLoading }: { day: DayType; isBranchDataLoading: boolean }) => {
  const { t } = useTranslation("profiles", { keyPrefix: "entityProfileEditBranch" });
  const { t: tDays } = useTranslation("common", { keyPrefix: "day" });
  const { control } = useFormContext<EntityBranchFormData>();

  return (
    <Controller
      name="workingHours"
      control={control}
      defaultValue={undefined}
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        const isActive = !!value?.find((wh) => wh.dayOfWeek.toLowerCase() === day);

        return (
          <Box sx={styles.dayRow}>
            <Box sx={styles.dayLabel}>
              <Checkbox
                size="small"
                checked={isActive}
                onChange={(e) => {
                  const workingHours = value || [];

                  if (!e.target.checked) {
                    onChange(workingHours.filter((wh) => wh.dayOfWeek.toLowerCase() !== day));
                  } else {
                    onChange([
                      ...workingHours,
                      {
                        id: 0,
                        dayOfWeek: day,
                        openingTime: "09:00",
                        closingTime: "17:00",
                        isClosed: false,
                      },
                    ]);
                  }
                }}
                disabled={isBranchDataLoading}
              />
              <FormLabel sx={styles.dayLabel}>{tDays(day)}</FormLabel>
            </Box>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <TimePicker
                disabled={!isActive || isBranchDataLoading}
                value={
                  value?.find((wh) => wh.dayOfWeek.toLowerCase() === day)?.openingTime
                    ? new Date(`1970-01-01T${value.find((wh) => wh.dayOfWeek.toLowerCase() === day)?.openingTime}`)
                    : null
                }
                onChange={(newValue) => {
                  if (newValue) {
                    const workingHours = value || [];
                    const newTime = formatTimeString(newValue);

                    onChange(
                      workingHours.map((wh) =>
                        wh.dayOfWeek.toLowerCase() === day ? { ...wh, openingTime: newTime } : wh
                      )
                    );
                  }
                }}
                slotProps={{
                  textField: {
                    error: !!error,
                    helperText: error?.message && t(error.message),
                    size: "small",
                    sx: {
                      width: "150px",
                    },
                  },
                }}
              />
            </LocalizationProvider>
            <Typography sx={styles.timeLabel}>{t("to")}</Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <TimePicker
                disabled={!isActive || isBranchDataLoading}
                value={
                  value?.find((wh) => wh.dayOfWeek.toLowerCase() === day)?.closingTime
                    ? new Date(`1970-01-01T${value.find((wh) => wh.dayOfWeek.toLowerCase() === day)?.closingTime}`)
                    : null
                }
                onChange={(newValue) => {
                  if (newValue) {
                    const workingHours = value || [];
                    const newTime = formatTimeString(newValue);

                    onChange(
                      workingHours.map((wh) =>
                        wh.dayOfWeek.toLowerCase() === day ? { ...wh, closingTime: newTime } : wh
                      )
                    );
                  }
                }}
                slotProps={{
                  textField: {
                    error: !!error,
                    helperText: error?.message && t(error.message),
                    size: "small",
                    sx: {
                      width: "150px",
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        );
      }}
    />
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    padding: "0 16px",
  },
  title: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  dayContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  dayRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  dayLabel: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    flex: 1,
  },
  timeContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  timeInput: {
    width: "120px",
  },
  timeLabel: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
};
